import * as React from 'react'

import { ArrowDownIcon, ArrowUpIcon } from 'lucide-react'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import Draggable from '~/client/src/shared/components/LazyDraggable'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { NOOP } from '~/client/src/shared/utils/noop'

import SelectButton from '../../SelectButton/SelectButton'
import { BasicDataKeys, LWFCRowData } from '../VirtualListHelpers'

interface IProps {
  dataKey: string
  label: string
  columnIndex: number
  toggleSelectionPopup: () => void
  onColumnSort?: (columnKey: string) => void
  sortedColumnKey?: string
  getCellClassNames?: (
    columnKey: string,
    data?: LWFCRowData,
  ) => { [className: string]: boolean }
  sortingOrder?: SortOrder
  isResizable: boolean
  onResizeColumn?: ({ columnIndex, deltaX }) => void
  style?: React.CSSProperties
  className?: string
  isUppercaseDisabled?: boolean
}

const SORT_INDICATION_ICON_SIZE = 8

@observer
export default class TableHeaderCell extends React.Component<IProps> {
  public static defaultProps = {
    getCellClassNames: NOOP,
    onColumnSort: NOOP,
    onResizeColumn: NOOP,
    className: '',
  }

  public render() {
    const {
      onColumnSort,
      sortedColumnKey,
      getCellClassNames,
      dataKey,
      label,
      columnIndex,
      isResizable,
      onResizeColumn,
      style,
      className,
      isUppercaseDisabled,
    } = this.props
    const isCheckboxColumn = dataKey === BasicDataKeys.CHECKBOX
    const isColumnSorted = sortedColumnKey === dataKey
    const isGroupedList = style !== undefined
    const handler = isCheckboxColumn ? NOOP : onColumnSort.bind(null, dataKey)

    return (
      <div
        className={classList({
          'row text no-outline-container': true,
          'cell cell-header pointer lp05 no-select': true,
          'full-height full-width relative bb-light-cool-grey': !isGroupedList,
          'bold bb-palette-grey': isGroupedList,
          'sorted bold white': isColumnSorted,
          'x-center': isCheckboxColumn,
          uppercase: !isUppercaseDisabled,
          [className]: true,
          ...getCellClassNames(dataKey),
        })}
        style={style}
      >
        {isCheckboxColumn ? (
          <div className="tw:flex tw:grow tw:justify-center">
            <SelectButton onClick={this.props.toggleSelectionPopup} />
          </div>
        ) : (
          <div onClick={handler}>
            {this.renderHeaderLabel(label, isColumnSorted)}
          </div>
        )}
        {isResizable && (
          <Draggable
            axis="x"
            defaultClassName="DragHandle"
            defaultClassNameDragging="DragHandleActive"
            onStop={(event, data) =>
              onResizeColumn({
                columnIndex,
                deltaX: data.x,
              })
            }
            position={{
              x: 0,
              y: 0,
            }}
          >
            <div className="resize-cursor resize-cursor-holder" />
          </Draggable>
        )}
      </div>
    )
  }

  private renderHeaderLabel = (
    label: string,
    isColumnSorted: boolean,
  ): JSX.Element => {
    const { sortingOrder } = this.props

    return (
      <>
        {label}
        {isColumnSorted && (
          <span className="pl2">
            {sortingOrder === SortOrder.ASC ? (
              <ArrowDownIcon
                size={SORT_INDICATION_ICON_SIZE}
                className="tw:align-middle"
              />
            ) : (
              <ArrowUpIcon
                size={SORT_INDICATION_ICON_SIZE}
                className="tw:align-middle"
              />
            )}
          </span>
        )}
      </>
    )
  }
}
